/**
 * The shared foreground privacy session (F-JP-04 … F-JP-07), as an imperative
 * store.
 *
 * It is deliberately **not** a React reducer. Every rule in this file is a
 * fail-closed rule that has to hold across `await` boundaries — "if already
 * authenticating, return false", "re-read availability after a failure", "a
 * reveal that lands after concealment is discarded" — and a `useState` closure
 * read inside an async callback is a snapshot of the state as it was when the
 * callback was created. One of those stale reads is a second system prompt; one
 * of them is a reveal completing into a concealed session. So the state lives
 * in an object with synchronous reads, and React subscribes to it.
 *
 * **One session is shared by every surface**, and that is the design rather
 * than a convenience. A recap composer opened from a finished workout cannot
 * bypass the lock, and an already-unlocked Journal opening an entry does not
 * prompt a second time, precisely because both ask this one object.
 *
 * The structural guarantee the whole packet rests on: the library is loaded
 * **after** verification succeeds and is dropped on the way out of `revealed`.
 * While the vault is not revealed there is no field anywhere in this store that
 * holds entry prose — not hidden, not filtered, not present.
 */

import type {
  JournalAuthenticationAvailability,
  JournalOpenLibrary,
  JournalRecoveryItem,
  JournalVault,
  JournalVaultEvent,
} from "@ff/domain";
import {
  JOURNAL_VAULT_INITIAL,
  journalForegroundLease,
  journalInstantFromMilliseconds,
  journalIsPrivacySensitive,
  journalOpenLibrary,
  reduceJournalVault,
} from "@ff/domain";
import type {
  Cancel,
  ClockPort,
  DeviceVerificationPort,
  JournalAuthReason,
  LifecyclePhase,
} from "@ff/domain/ports";
import type { JournalLockChangeResult } from "./lockChange";
import { authorizeJournalLockChange } from "./lockChange";
import { JournalPreviewVault } from "./previews";

/**
 * The result of asking the repository for everything a revealed Journal reads.
 *
 * A failure carries recoveries because they are metadata — id, kind, reason,
 * byte count, digest — and never decoded content. Surfacing them from an
 * unavailable store is what lets a user act on corruption; surfacing an empty
 * list instead would tell them their journal is gone.
 */
export type JournalLibraryLoad =
  | { readonly kind: "loaded"; readonly library: JournalOpenLibrary }
  | {
      readonly kind: "failed";
      readonly message: string;
      readonly recoveries: readonly JournalRecoveryItem[];
    };

export type JournalLibraryLoader = () => Promise<JournalLibraryLoad>;

/**
 * Handed to `reduceJournalVault` on the locked preparation path, where the
 * reducer discards it and returns `concealed`.
 *
 * The alternative would be to load the real library and let the reducer throw
 * it away — which would mean every entry's prose passing through memory on a
 * cold start of a locked journal, for no reason at all. So the loader is not
 * called, and this stands in for the argument the event's shape requires. It is
 * an empty value, not a redacted one: there is nothing in it to redact.
 */
const NO_LIBRARY: JournalOpenLibrary = Object.freeze({
  projections: [],
  standaloneDrafts: [],
  goals: [],
  recoveries: [],
});

export interface PrivacySessionSnapshot {
  readonly vault: JournalVault;
  /**
   * The last value read from the verification port. Kept beside the vault
   * because `preparing` and `unavailable` have nowhere to hold it, and the
   * Journal-privacy settings screen still has to say what the device can do.
   */
  readonly availability: JournalAuthenticationAvailability;
  /** The persisted setting, never an optimistic copy. */
  readonly lockEnabled: boolean;
  readonly isChangingLock: boolean;
  readonly isJournalNavigationActive: boolean;
  readonly isComposerPresented: boolean;
}

export interface PrivacySessionControllerOptions {
  readonly verification: DeviceVerificationPort;
  readonly clock: ClockPort;
  readonly loadLibrary: JournalLibraryLoader;
  readonly readLockEnabled: () => boolean;
  readonly writeLockEnabled: (lockEnabled: boolean) => boolean;
  readonly previews?: JournalPreviewVault;
  /**
   * Releases the passphrase-derived key. Called on foreground loss, at the same
   * moment the lease is revoked and the library dropped — the three have the
   * same lifetime and separating them is how one of them survives the other two.
   */
  readonly dropKeyMaterial?: () => void;
}

export class PrivacySessionController {
  readonly #verification: DeviceVerificationPort;
  readonly #clock: ClockPort;
  readonly #loadLibrary: JournalLibraryLoader;
  readonly #readLockEnabled: () => boolean;
  readonly #writeLockEnabled: (lockEnabled: boolean) => boolean;
  readonly #previews: JournalPreviewVault;
  readonly #dropKeyMaterial: () => void;

  #listeners = new Set<() => void>();
  #snapshot: PrivacySessionSnapshot;
  /**
   * Claimed synchronously by {@link reveal} for the whole ceremony.
   *
   * Complements {@link #revealGeneration}: the generation invalidates work that
   * is no longer wanted, this stops a second ceremony from starting at all.
   */
  #revealInFlight = false;
  #navigationHolders = 0;
  #composerHolders = 0;
  /**
   * Invalidates in-flight work. A reveal that was authenticating when the page
   * was hidden must not complete into the concealed session it left behind —
   * `reduceJournalVault` accepts `revealSucceeded` from `concealed` (that is
   * how an already-authenticated surface reveals without a second prompt), so
   * without this guard a late resolution would reveal a journal the user had
   * already walked away from.
   */
  #revealGeneration = 0;
  #phase: LifecyclePhase = "visible";

  constructor(options: PrivacySessionControllerOptions) {
    this.#verification = options.verification;
    this.#clock = options.clock;
    this.#loadLibrary = options.loadLibrary;
    this.#readLockEnabled = options.readLockEnabled;
    this.#writeLockEnabled = options.writeLockEnabled;
    this.#previews = options.previews ?? new JournalPreviewVault();
    this.#dropKeyMaterial = options.dropKeyMaterial ?? (() => {});

    this.#snapshot = {
      vault: JOURNAL_VAULT_INITIAL,
      // Fail closed until the first probe answers. Anything else would let a
      // lock-change authorization run for a moment on an optimistic default.
      availability: "unavailable",
      lockEnabled: this.#readLockEnabled(),
      isChangingLock: false,
      isJournalNavigationActive: false,
      isComposerPresented: false,
    };
  }

  // --- reading ------------------------------------------------------------

  snapshot(): PrivacySessionSnapshot {
    return this.#snapshot;
  }

  subscribe(listener: () => void): Cancel {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  previews(): JournalPreviewVault {
    return this.#previews;
  }

  /** The only path to entry content, delegated to the domain's one accessor. */
  library(): JournalOpenLibrary | null {
    return journalOpenLibrary(this.#snapshot.vault);
  }

  isUnlocked(): boolean {
    return this.#snapshot.vault.state === "revealed";
  }

  isAuthenticating(): boolean {
    return this.#snapshot.vault.state === "authenticating";
  }

  hasVerifiedForegroundLease(): boolean {
    return journalForegroundLease(this.#snapshot.vault) !== null;
  }

  isPrivacySensitive(): boolean {
    return journalIsPrivacySensitive({
      isJournalNavigationActive: this.#snapshot.isJournalNavigationActive,
      isComposerPresented: this.#snapshot.isComposerPresented,
    });
  }

  // --- lifecycle ----------------------------------------------------------

  /**
   * The cold start. Reads the persisted setting and the device's capability,
   * then either reveals an unlocked journal or comes up concealed.
   *
   * With the lock on the loader is never called. That is the difference between
   * "the UI does not show it" and "it is not here".
   */
  async start(): Promise<void> {
    const lockEnabled = this.#readLockEnabled();
    this.#mutate({ lockEnabled });
    const availability = await this.#readAvailability();

    if (lockEnabled) {
      this.#dispatch({
        type: "prepared",
        library: NO_LIBRARY,
        lockEnabled: true,
        availability,
      });
      return;
    }

    const load = await this.#loadLibrary();
    if (load.kind === "failed") {
      this.#dispatch({
        type: "preparationFailed",
        message: load.message,
        recoveries: load.recoveries,
      });
      return;
    }
    this.#dispatch({
      type: "prepared",
      library: load.library,
      lockEnabled: false,
      availability,
    });
  }

  /**
   * The lifecycle phase from `VisibilityPort`.
   *
   * Called **from the event handler**, so the preview sweep and the key drop
   * happen on the last line the page runs before the browser may capture it.
   * Deferring either to a React effect would put them a render after the frame
   * that gets photographed.
   */
  handlePhase(phase: LifecyclePhase): void {
    this.#phase = phase;
    if (phase === "visible") {
      // F-JP-02 re-reads availability on activation: the user may have just
      // come back from fixing a device passcode, and a cached "unavailable"
      // would strand them on the recovery copy.
      void this.#readAvailability();
      return;
    }

    // Order matters. Previews and key material go first because they are
    // imperative and immediate; the vault transition that follows is what React
    // will eventually render.
    this.#previews.revokeAll();
    this.#dropKeyMaterial();
    this.#revealGeneration += 1;
    this.#dispatch({
      type: "foregroundLost",
      lockEnabled: this.#snapshot.lockEnabled,
    });
  }

  // --- the unlock gate (F-JP-05) -----------------------------------------

  /**
   * The single unlock gate every surface goes through.
   *
   * With the lock off this lifts concealment with no authentication and — the
   * part that matters — **no lease**. Acknowledging a journal that was never
   * locked verifies nothing, and treating it as verification would let someone
   * with no device passcode turn the lock off on another person's device.
   */
  async reveal(reason: JournalAuthReason = "openJournal"): Promise<boolean> {
    const vault = this.#snapshot.vault;
    if (vault.state === "revealed") return true;
    // A second concurrent attempt is not a second decision. Returning false
    // here is what stops two surfaces from raising two system prompts.
    //
    // The vault state alone cannot carry this guard: it does not become
    // `authenticating` until after the availability read, so two callers
    // entering in the same turn would both still see `concealed` and both go on
    // to raise a prompt. The latch is set synchronously, before any await, so
    // there is no window between the check and the claim.
    if (vault.state === "authenticating" || this.#revealInFlight) return false;
    if (vault.state !== "concealed") return false;

    this.#revealInFlight = true;
    try {
      // `vault` is already narrowed to the concealed case here; passing it in
      // keeps that narrowing rather than re-reading the union on the far side.
      return await this.#reveal(reason, vault);
    } finally {
      this.#revealInFlight = false;
    }
  }

  async #reveal(
    reason: JournalAuthReason,
    vault: Extract<JournalVault, { readonly state: "concealed" }>,
  ): Promise<boolean> {
    const generation = this.#revealGeneration;

    if (vault.resume.kind === "requiresAcknowledgement") {
      const load = await this.#loadLibrary();
      if (generation !== this.#revealGeneration) return false;
      if (load.kind === "failed") {
        this.#dispatch({
          type: "preparationFailed",
          message: load.message,
          recoveries: load.recoveries,
        });
        return false;
      }
      this.#dispatch({ type: "revealAcknowledged", library: load.library });
      return true;
    }

    const availability = await this.#readAvailability();
    if (generation !== this.#revealGeneration) return false;
    // Fail closed, and without prompting: there is nothing to ask, and a prompt
    // that cannot verify would either no-op or look like it succeeded.
    if (availability === "unavailable") return false;

    this.#dispatch({ type: "revealRequested" });
    // The reducer refuses the transition if anything moved underneath us —
    // there is no state in which we should then run a ceremony.
    if (this.#snapshot.vault.state !== "authenticating") return false;

    const verified = await this.#verification.verify(reason);
    if (generation !== this.#revealGeneration) return false;

    if (!verified) {
      // Re-read after any failed authentication (F-JP-02): a removed passcode
      // presents to the page as a declined prompt, and the curtain's next
      // message depends on knowing which it was.
      const fresh = await this.#readAvailability();
      this.#dispatch({ type: "revealFailed", availability: fresh });
      return false;
    }

    // Only now is there anything to load. Everything above this line runs with
    // no entry content in the process.
    const load = await this.#loadLibrary();
    if (generation !== this.#revealGeneration) return false;
    if (load.kind === "failed") {
      this.#dispatch({
        type: "preparationFailed",
        message: load.message,
        recoveries: load.recoveries,
      });
      return false;
    }

    this.#dispatch({
      type: "revealSucceeded",
      at: journalInstantFromMilliseconds(this.#clock.now()),
      verifiedBy: availability,
      library: load.library,
    });
    return true;
  }

  /** "Not now" on the curtain. Returns to concealed without touching anything. */
  cancelReveal(): void {
    this.#revealGeneration += 1;
    this.#dispatch({ type: "revealCancelled" });
  }

  /** Re-reads the library into an already-revealed session. */
  async refreshLibrary(): Promise<boolean> {
    if (!this.isUnlocked()) return false;
    const generation = this.#revealGeneration;
    const load = await this.#loadLibrary();
    if (generation !== this.#revealGeneration) return false;
    if (load.kind === "failed") {
      this.#dispatch({
        type: "preparationFailed",
        message: load.message,
        recoveries: load.recoveries,
      });
      return false;
    }
    // A refresh can only replace content that is already legitimately open, so
    // this is a no-op if the session concealed while the read was in flight.
    this.#dispatch({ type: "libraryRefreshed", library: load.library });
    return true;
  }

  /** "Check again" on the recovery block, and every other availability re-read. */
  async refreshAvailability(): Promise<JournalAuthenticationAvailability> {
    return this.#readAvailability();
  }

  // --- the lock setting (F-JP-01, F-JP-06) --------------------------------

  async changeLock(requested: boolean): Promise<JournalLockChangeResult> {
    if (this.#snapshot.isChangingLock) {
      // Re-entrancy guard. A second request while one is in flight is not a
      // second decision, and letting it through would raise a second system
      // prompt for a change that is already being authorized.
      return {
        outcome: { kind: "noChange" },
        availability: this.#snapshot.availability,
        persisted: false,
      };
    }

    this.#mutate({ isChangingLock: true });
    try {
      const result = await authorizeJournalLockChange({
        current: this.#readLockEnabled(),
        requested,
        isUnlocked: this.isUnlocked(),
        hasVerifiedForegroundLease: this.hasVerifiedForegroundLease(),
        readAvailability: () => this.#readAvailability(),
        verify: (reason) => this.#verification.verify(reason),
        persist: (lockEnabled) => this.#writeLockEnabled(lockEnabled),
      });

      if (result.persisted) await this.applyLockEnabled(requested);
      return result;
    } finally {
      this.#mutate({ isChangingLock: false });
    }
  }

  /**
   * Reacts to the persisted setting's new value — from this tab's own change,
   * or from another tab of the same profile writing it.
   *
   * Turning it **on** conceals immediately, even mid-composition: the user just
   * asked for the content to stop being visible. Turning it **off** unlocks
   * immediately, which on the web means handing the library back in, because
   * the concealed state never held it.
   */
  async applyLockEnabled(lockEnabled: boolean): Promise<void> {
    if (this.#snapshot.lockEnabled === lockEnabled) return;
    this.#mutate({ lockEnabled });
    this.#dispatch({ type: "lockEnabledChanged", lockEnabled });

    if (lockEnabled) {
      // Nothing derived from the journal may outlive the lock going on.
      this.#previews.revokeAll();
      return;
    }
    // Only while the page is actually in front of the user. Unlocking a
    // backgrounded tab would load the library into a document that is already
    // being snapshotted by the OS.
    if (this.#phase !== "visible") return;
    await this.reveal();
  }

  // --- privacy sensitivity (F-JP-14) --------------------------------------

  /**
   * Registers an open Journal destination or composer. Counted rather than set
   * to a boolean: two surfaces can legitimately be sensitive at once — a
   * composer presented over the Journal — and the last one to close would
   * otherwise clear a flag the other still needs.
   */
  retainJournalNavigation(): Cancel {
    this.#navigationHolders += 1;
    this.#syncSensitivity();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.#navigationHolders -= 1;
      this.#syncSensitivity();
    };
  }

  retainComposer(): Cancel {
    this.#composerHolders += 1;
    this.#syncSensitivity();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.#composerHolders -= 1;
      this.#syncSensitivity();
    };
  }

  // --- internals ----------------------------------------------------------

  async #readAvailability(): Promise<JournalAuthenticationAvailability> {
    let availability: JournalAuthenticationAvailability;
    try {
      availability = await this.#verification.availability();
    } catch {
      // The port is documented never to reject, but this decision is the one
      // that decides whether a journal can be unlocked — it does not get to
      // depend on every future adapter honouring that.
      availability = "unavailable";
    }
    if (availability !== this.#snapshot.availability) {
      this.#mutate({ availability });
      this.#dispatch({ type: "availabilityChanged", availability });
    }
    return availability;
  }

  #syncSensitivity(): void {
    this.#mutate({
      isJournalNavigationActive: this.#navigationHolders > 0,
      isComposerPresented: this.#composerHolders > 0,
    });
  }

  #dispatch(event: JournalVaultEvent): void {
    const next = reduceJournalVault(this.#snapshot.vault, event);
    if (next === this.#snapshot.vault) return;
    this.#mutate({ vault: next });
  }

  #mutate(patch: Partial<PrivacySessionSnapshot>): void {
    const next: PrivacySessionSnapshot = { ...this.#snapshot, ...patch };
    if (
      next.vault === this.#snapshot.vault &&
      next.availability === this.#snapshot.availability &&
      next.lockEnabled === this.#snapshot.lockEnabled &&
      next.isChangingLock === this.#snapshot.isChangingLock &&
      next.isJournalNavigationActive === this.#snapshot.isJournalNavigationActive &&
      next.isComposerPresented === this.#snapshot.isComposerPresented
    ) {
      // `useSyncExternalStore` re-renders on snapshot identity, so an unchanged
      // state must keep the same object or every no-op probe becomes a render.
      return;
    }
    this.#snapshot = next;
    for (const listener of [...this.#listeners]) listener();
  }
}

/**
 * F-HS-25's precondition, as a predicate rather than as copy.
 *
 * The hard contract is that **no journal text may appear on a training surface
 * while Journal Lock is on and the session is not unlocked**. The Train surface
 * owns the fallback wording; what it must not own is the rule, because a second
 * statement of it is a second place it can be got wrong — and getting it wrong
 * here means a reflection on a screen the user did not unlock.
 */
export function journalPreviewIsRevealable(input: {
  readonly lockEnabled: boolean;
  readonly isUnlocked: boolean;
  readonly libraryAvailable: boolean;
  readonly hasEntries: boolean;
}): boolean {
  return (
    input.libraryAvailable &&
    (!input.lockEnabled || input.isUnlocked) &&
    input.hasEntries
  );
}
